import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Button,
  alpha,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { TemplateSummary } from 'types/cx-editor';
import { DataScenario, DataScenariosApi } from 'apis/dataScenariosAPI';
import { useGlobalConfig } from 'context/GlobalContext';
import { toast } from 'utils/toast';
import { AddDataScenarios } from './AddDataScenarios';
import { CloneDataScenarios } from './CloneDataScenarios';
import { EditDataScenarios } from './EditDataScenarios';

interface DetectedScenario {
  id: string;
  name: string;
  identifier: string;
}

interface SelectedSection {
  id: string;
  name: string;
}

type Props = {
  selectedTemplate: TemplateSummary | null;
  selectedSection?: SelectedSection;
  scenarios?: DataScenario[];
  loading?: boolean;
  error?: string | null;
  pilotId?: string;
  isGlobal?: boolean; // When true, shows "Add Global Scenario" instead of "Add Project Scenario"
  onImportGlobal?: () => void;
  onAddProjectScenario?: () => void;
  onCopyScenario?: (scenarioId: string) => void;
  onEditScenario?: (scenarioId: string) => void;
  onDeleteScenario?: (scenarioId: string) => void;
};

// Convert fuelType to display label
const getFuelTypeLabel = (fuelType: string): string => {
  const normalizedFuelType = fuelType?.trim().toUpperCase() || '';
  switch (normalizedFuelType) {
    case 'ELECTRIC':
      return 'EL';
    case 'GAS':
      return 'GAS';
    case 'DUAL':
    case 'DUAL_FUEL':
    case 'DUALFUEL':
      return 'DF';
    default:
      return normalizedFuelType.substring(0, 3).toUpperCase() || 'EL';
  }
};

// Valid fuel types to skip when parsing display name
const VALID_FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER', 'DUAL', 'DUAL_FUEL', 'DUALFUEL'];

// Extract tag from template name (e.g., "(EL) Monthly Summary" -> "EL")
const getTemplateTag = (templateName: string): string => {
  const match = templateName.match(/^\(([^)]+)\)/);
  return match ? match[1] : '';
};

// Get display name - parse raw template key or remove tag prefix
const getTemplateDisplayName = (templateName: string): string => {
  const tagMatch = templateName.match(/^\([^)]+\)\s*(.+)/);
  if (tagMatch) {
    return tagMatch[1];
  }
  
  if (templateName.includes('.')) {
    const parts = templateName.split('.');
    
    // Find the meaningful part (skip product prefix and fuel suffix)
    // Example: EMAIL.DF.BILL_PROJECTION.ELECTRIC -> BILL_PROJECTION -> "Bill Projection"
    let meaningfulPart = '';
    
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i].toUpperCase();
      // Skip fuel types and common prefixes
      if (VALID_FUEL_TYPES.includes(part) || ['EMAIL', 'WEB', 'HER', 'SMS', 'DF', 'SF'].includes(part)) {
        continue;
      }
      meaningfulPart = parts[i];
      break;
    }
    
    if (!meaningfulPart) {
      meaningfulPart = parts[parts.length - 1];
    }
    
    return meaningfulPart
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  return templateName;
};

export const TemplateLevelScenarios: React.FC<Props> = ({
  selectedTemplate,
  selectedSection,
  scenarios = [],
  loading = false,
  error = null,
  pilotId = '',
  isGlobal = false,
  onImportGlobal,
  onAddProjectScenario,
  onCopyScenario,
  onEditScenario,
  onDeleteScenario
}) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [showScenariosList, setShowScenariosList] = useState(false);
  const [detectedScenarios, setDetectedScenarios] = useState<DetectedScenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<Set<string>>(new Set());
  const [isLoadingScenarios, setIsLoadingScenarios] = useState(false);
  const [addScenarioModalOpen, setAddScenarioModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [selectedScenarioForClone, setSelectedScenarioForClone] = useState<DataScenario | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedScenarioForEdit, setSelectedScenarioForEdit] = useState<DataScenario | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedScenarioForDelete, setSelectedScenarioForDelete] = useState<DataScenario | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteScenario = async () => {
    if (!selectedScenarioForDelete || !pilotId) return;
    
    setIsDeleting(true);
    try {
      const response = await DataScenariosApi.deleteDataScenario(rc, {
        pilotId: pilotId,
        scenarioId: selectedScenarioForDelete.scenarioId
      });
      
      if (response.success) {
        toast.success(`Scenario "${selectedScenarioForDelete.scenarioDisplayName}" deleted successfully`);
        onDeleteScenario?.(String(selectedScenarioForDelete.id));
        setDeleteModalOpen(false);
        setSelectedScenarioForDelete(null);
      } else {
        toast.error(response.error || 'Failed to delete scenario');
      }
    } catch (error: any) {
      console.error('[TemplateLevelScenarios] Error deleting scenario:', error);
      toast.error(error.message || 'Failed to delete scenario');
    } finally {
      setIsDeleting(false);
    }
  };

  // Mock data for detected scenarios
  const mockDetectedScenarios: DetectedScenario[] = [
    { id: '1', name: 'Default', identifier: 'electric-resi-default' },
    { id: '2', name: 'TOU Rate', identifier: 'electric-resi-tourate' },
    { id: '3', name: 'Budget Bill (BB)', identifier: 'electric-resi-bb' },
    { id: '4', name: 'Solar', identifier: 'electric-resi-solar' },
    { id: '5', name: 'Solar + TOU', identifier: 'electric-resi-solar+tou' },
    { id: '6', name: 'Solar+BB', identifier: 'electric-resi-solar+bb' },
    { id: '7', name: 'Bill higher than previous', identifier: 'electric-resi-higher-bill' },
    { id: '8', name: 'Bill lower than previous', identifier: 'electric-resi-lower-bill' },
    { id: '9', name: 'Budget Bill - higher than previous', identifier: 'electric-resi-budget-bill-higher' },
    { id: '10', name: 'Budget Bill - lower than previous', identifier: 'electric-resi-budget-bill-lower' },
    { id: '11', name: 'High Usage Alert', identifier: 'electric-resi-high-usage' },
    { id: '12', name: 'Low Usage Alert', identifier: 'electric-resi-low-usage' },
    { id: '13', name: 'Peak Demand', identifier: 'electric-resi-peak-demand' },
    { id: '14', name: 'Off-Peak Savings', identifier: 'electric-resi-off-peak' }
  ];

  const handleOpenImportModal = () => {
    setImportModalOpen(true);
    setShowScenariosList(false);
    setSelectedScenarios(new Set());
    onImportGlobal?.();
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setShowScenariosList(false);
    setSelectedScenarios(new Set());
    setDetectedScenarios([]);
  };

  const handleCheckAvailableScenarios = async () => {
    setIsLoadingScenarios(true);
    // Simulate API call
    setTimeout(() => {
      setDetectedScenarios(mockDetectedScenarios);
      setShowScenariosList(true);
      setIsLoadingScenarios(false);
    }, 500);
  };

  const handleToggleScenario = (scenarioId: string) => {
    setSelectedScenarios(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scenarioId)) {
        newSet.delete(scenarioId);
      } else {
        newSet.add(scenarioId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedScenarios(new Set(detectedScenarios.map(s => s.id)));
    } else {
      setSelectedScenarios(new Set());
    }
  };

  const handleImportSelected = () => {
    console.log('Importing scenarios:', Array.from(selectedScenarios));
    // TODO: Implement actual import logic
    handleCloseImportModal();
  };

  const isAllSelected = detectedScenarios.length > 0 && selectedScenarios.size === detectedScenarios.length;
  const isIndeterminate = selectedScenarios.size > 0 && selectedScenarios.size < detectedScenarios.length;

  if (!selectedTemplate) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100%'
      }}>
        <Typography variant="body2" color="text.secondary">
          Select a template to view its scenarios
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
      {/* Template Header */}
      <Card sx={{ 
        borderRadius: 2,
        boxShadow: 1,
        bgcolor: theme.palette.background.paper
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {getTemplateTag(selectedTemplate.name) && (
              <Chip
                label={getTemplateTag(selectedTemplate.name)}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.5,
                  borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                  color: theme => theme.palette.primary.main,
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                }}
              />
            )}
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {getTemplateDisplayName(selectedTemplate.name)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
            {isGlobal 
              ? 'Manage global data scenarios for this template and its elements.'
              : 'Manage project data scenarios for this template and its elements.'}
          </Typography>
        </CardContent>
      </Card>

      {/* Template-Level Scenarios Section */}
      <Card sx={{ 
        flex: 1,
        borderRadius: 2,
        boxShadow: 1,
        bgcolor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 3 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2
            }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {selectedSection ? `Element Scenarios: ${selectedSection.name}` : 'Template-Level Scenarios'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isGlobal && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleOpenImportModal}
                disabled
                sx={{ textTransform: 'none' }}
              >
                Import Global
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddScenarioModalOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              {isGlobal ? 'Add Global Scenario' : 'Add Project Scenario'}
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Loading scenarios…
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 1
          }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {/* Scenarios List */}
        {!loading && !error && (
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>
            {scenarios.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flex: 1
              }}>
                <Typography variant="body2" color="text.secondary">
                  No scenarios found for this Template/Element
                </Typography>
              </Box>
            ) : (
              scenarios.map((scenario) => (
                <Box
                  key={scenario.id}
                  sx={{
                    p: 0.8,
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    '&:last-child': { mb: 0 }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 400, 
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {scenario.scenarioDisplayName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {/* Scope chip - Global or Project */}
                      <Chip
                        label={isGlobal ? 'Global' : 'Project'}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                          borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                          color: theme => theme.palette.primary.main,
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                        }}
                      />
                      {/* Fuel Type chip (EL, GAS, DF) */}
                      <Chip
                        label={getFuelTypeLabel(scenario.fuelType)}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                          borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                          color: theme => theme.palette.primary.main,
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                        }}
                      />
                      {/* Locale chip */}
                      <Chip
                        label={scenario.scenarioLocale}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                          borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                          color: theme => theme.palette.primary.main,
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 2, flexShrink: 0 }}>
                    <Tooltip title="Clone Scenario">
                      <span>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            p: 0.75,
                            color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit'
                          }}
                          disabled
                          onClick={() => {
                            setSelectedScenarioForClone(scenario);
                            setCloneModalOpen(true);
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        sx={{ 
                          p: 0.75,
                          color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit'
                        }}
                        onClick={() => {
                          setSelectedScenarioForEdit(scenario);
                          setEditModalOpen(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        sx={{ p: 0.75, color: theme.palette.error.main }}
                        onClick={() => {
                          setSelectedScenarioForDelete(scenario);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}
          </Box>
        </CardContent>
      </Card>

      {/* Import Global Scenarios Modal */}
      <Dialog
        open={importModalOpen}
        onClose={handleCloseImportModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3,
            maxWidth: '800px',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DownloadIcon sx={{ fontSize: 24, color: theme.palette.text.primary }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Import Global Scenarios
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseImportModal}
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
            Scan for and import existing global scenarios into your current project.
          </Typography>

          {!showScenariosList ? (
            <Box
              sx={{
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: theme.palette.background.default,
                mb: 2
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 3, textAlign: 'center' }}
              >
                Click the button below to scan all templates for available global data scenarios.
              </Typography>
              <Button
                variant="contained"
                size="medium"
                onClick={handleCheckAvailableScenarios}
                disabled={isLoadingScenarios}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1
                }}
              >
                {isLoadingScenarios ? 'Scanning...' : 'Check available Global scenarios'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  Detected Global Scenarios ({detectedScenarios.length})
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSvgIcon-root': {
                          borderRadius: '4px'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Select All
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Box>
              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  borderRadius: 1
                }}
              >
                <List disablePadding>
                  {detectedScenarios.map((scenario) => (
                    <ListItem
                      key={scenario.id}
                      disablePadding
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedScenarios.has(scenario.id)}
                        onChange={() => handleToggleScenario(scenario.id)}
                        size="small"
                        sx={{ 
                          mr: 1,
                          '& .MuiSvgIcon-root': {
                            borderRadius: '4px'
                          }
                        }}
                      />
                      <ListItemButton
                        onClick={() => handleToggleScenario(scenario.id)}
                        sx={{ 
                          py: 0.75,
                          px: 1,
                          '&:hover': {
                            backgroundColor: 'transparent'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                            {scenario.name}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
                            ({scenario.identifier})
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseImportModal}
            sx={{ 
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          {showScenariosList && (
            <Button
              variant="contained"
              onClick={handleImportSelected}
              disabled={selectedScenarios.size === 0}
              startIcon={<DoneAllIcon />}
              sx={{ 
                textTransform: 'none'
              }}
            >
              Import {selectedScenarios.size} Scenario{selectedScenarios.size !== 1 ? 's' : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Data Scenarios Modal */}
      <AddDataScenarios
        open={addScenarioModalOpen}
        onClose={() => setAddScenarioModalOpen(false)}
        onSave={() => {
          onAddProjectScenario?.();
        }}
        pilotId={pilotId}
        templateKey={selectedTemplate?.templateKey || ''}
        templateDisplayName={selectedTemplate ? getTemplateDisplayName(selectedTemplate.name) : ''}
        elementKey={selectedSection?.id}
        elementDisplayName={selectedSection?.name || 'Full Template'}
      />

      {/* Clone Data Scenarios Modal */}
      <CloneDataScenarios
        open={cloneModalOpen}
        onClose={() => {
          setCloneModalOpen(false);
          setSelectedScenarioForClone(null);
        }}
        scenarioName={selectedScenarioForClone?.scenarioDisplayName || ''}
        onCopy={(selectedTargets) => {
          console.log('Copying scenario to targets:', selectedTargets);
          onCopyScenario?.(String(selectedScenarioForClone?.id) || '');
        }}
      />

      {/* Edit Data Scenarios Modal */}
      <EditDataScenarios
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedScenarioForEdit(null);
        }}
        pilotId={pilotId}
        scenarioData={selectedScenarioForEdit ? {
          scenarioId: selectedScenarioForEdit.scenarioId,
          label: selectedScenarioForEdit.scenarioDisplayName,
          configLevel: isGlobal ? 'Global' : 'Project',
          templateType: selectedScenarioForEdit.productType || 'Email',
          locale: selectedScenarioForEdit.scenarioLocale,
          templateName: selectedScenarioForEdit.templateName,
          elementName: selectedScenarioForEdit.elementNames?.[0] || 'FULL_TEMPLATE',
          elementNames: selectedScenarioForEdit.elementNames || ['FULL_TEMPLATE'],
          fuelType: selectedScenarioForEdit.fuelType || 'ELECTRIC',
          payloads: [
            {
              endpoint: selectedScenarioForEdit.productType || 'EMAIL',
              response: JSON.stringify({
                message: 'Edit payload to add scenario data',
                scenarioId: selectedScenarioForEdit.scenarioId,
                fuelType: selectedScenarioForEdit.fuelType?.toLowerCase() || 'electric'
              }, null, 2)
            }
          ]
        } : undefined}
        onSave={() => {
          console.log('Scenario updated successfully');
          onEditScenario?.(String(selectedScenarioForEdit?.id) || '');
          setEditModalOpen(false);
          setSelectedScenarioForEdit(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setSelectedScenarioForDelete(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Delete Scenario
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete the scenario{' '}
            <strong>"{selectedScenarioForDelete?.scenarioDisplayName}"</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone. All associated payloads will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteModalOpen(false);
              setSelectedScenarioForDelete(null);
            }}
            disabled={isDeleting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteScenario}
            disabled={isDeleting}
            sx={{ textTransform: 'none', minWidth: 80 }}
          >
            {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

